/**
 * App shell. Desktop (>= 1024 px): sidebar + top bar. Tablet: icon rail that
 * opens as a drawer. Phone (< 768 px): compact top bar + bottom tab bar with a
 * "Meer" sheet. Which parts show is decided in CSS (no layout shift); JS only
 * holds the open/collapsed state.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Toaster } from '../components/Toast.js';
import { useIsDesktop } from '../lib/media.js';
import { usePref } from '../lib/prefs.js';
import { hasRole, useMe } from '../lib/session.js';
import styles from './AppShell.module.css';
import { MoreSheet } from './MoreSheet.js';
import { NAV_ITEMS } from './nav.js';
import { Sidebar } from './Sidebar.js';
import { StatusBanners } from './StatusBanners.js';
import { TabBar } from './TabBar.js';
import { TopBar } from './TopBar.js';

export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = usePref('tl.sidebarCollapsed', false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const items = NAV_ITEMS.filter((i) => hasRole(me, i.role ?? 'viewer'));

  useEffect(() => {
    setDrawerOpen(false);
    setMoreOpen(false);
  }, [pathname]);
  // A layout effect, so it runs before the pages' own effects: "Mijn planning" scrolls to today
  // in a regular effect, which must not be undone when its data is already cached.
  useLayoutEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const toggleSidebar = () => (isDesktop ? setCollapsed(!collapsed) : setDrawerOpen((o) => !o));

  return (
    <div className={styles.shell} data-collapsed={collapsed}>
      <a href="#main" className={styles.skipLink}>
        Naar de inhoud
      </a>
      <Sidebar items={items} open={drawerOpen} collapsed={collapsed} onToggle={toggleSidebar} />
      {drawerOpen && <div className={styles.drawerBackdrop} onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <TopBar onMenu={() => setDrawerOpen((o) => !o)} menuOpen={drawerOpen} />
      <main id="main" className={styles.main} tabIndex={-1}>
        <StatusBanners />
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
      </main>
      <TabBar items={items} moreActive={moreOpen} onMore={() => setMoreOpen(true)} />
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} items={items} />
      <Toaster />
    </div>
  );
}
